import {
  classifyAreaBranchFee,
  compareFeeWithMedian,
  getFeeAtVatBasis,
  getFeeCost,
  getPercentFeeAnnualCost,
  getPositionInRange,
  getServiceFee,
  hasAnyPublishedFee,
  parseFeePercent,
  summarizeAreaFees,
  type AreaFeeService,
  type AreaStatsForSummary,
  type BranchFees,
  type ComparableFee,
} from '@/utils/fee-maths';

import {
  formatFeeBandHeadline,
  getDirectoryFeeBandThresholds,
  getFeeBand,
  type FeeBand,
} from './directory-fee-band';
import { formatFeePercent } from './directory-fee-display';

const minAreaSampleSize = 3;

export type DirectoryAgentCostService = Extract<
  AreaFeeService,
  'management' | 'rentCollection'
>;

type PercentServiceStats = {
  sampleSize?: number | null;
  minimumPercentIncVat?: number | null;
  medianPercentIncVat?: number | null;
  maximumPercentIncVat?: number | null;
  percentsIncVat?: readonly number[] | null;
};

type DirectoryAgentCostComparison = {
  differencePence: number;
  differencePoints: number;
  band: FeeBand;
  medianPercentIncVat: number;
  minimumPercentIncVat: number;
  maximumPercentIncVat: number;
  medianPosition: number;
  agentPosition: number;
  percentsIncVat: readonly number[];
  agenciesHigher: number;
  agenciesLower: number;
  dotPlotAriaLabel: string;
};

export type DirectoryAgentCostView =
  | {
      kind: 'noFees';
      areaName: string;
      medianPercentIncVat: number;
      medianCostPence: number;
      minimumPercentIncVat: number;
      maximumPercentIncVat: number;
      medianPosition: number;
      percentsIncVat: readonly number[];
    }
  | {
      kind: 'serviceNotPublished';
      areaName: string | null;
    }
  | {
      kind: 'notComparable';
      reason: 'vatNotStated' | 'periodNotStated';
      areaName: string | null;
      publishedPercent: number | null;
      publishedAmountPence: number | null;
    }
  | {
      kind: 'comparable';
      areaName: string | null;
      percentIncVat: number;
      publishedPercentExVat: number | null;
      isVatAdded: boolean;
      costPence: number;
      comparison: DirectoryAgentCostComparison | null;
      band: FeeBand | null;
    };

function getPercentStats(
  stats: AreaStatsForSummary | null | undefined,
  service: DirectoryAgentCostService,
): PercentServiceStats | null {
  if (stats == null) {
    return null;
  }

  return service === 'management'
    ? (stats.management ?? null)
    : (stats.rentCollection ?? null);
}

function buildComparison(options: {
  agentName: string;
  areaName: string;
  fee: Extract<ComparableFee, { status: 'comparable' }>;
  monthlyRentPence: number;
  stats: PercentServiceStats;
}): DirectoryAgentCostComparison | null {
  const { agentName, areaName, fee, monthlyRentPence, stats } = options;
  const sampleSize = stats.sampleSize ?? 0;
  const median = stats.medianPercentIncVat;
  const minimum = stats.minimumPercentIncVat;
  const maximum = stats.maximumPercentIncVat;
  const percents = stats.percentsIncVat;

  if (
    sampleSize < minAreaSampleSize ||
    median == null ||
    minimum == null ||
    maximum == null ||
    percents == null ||
    percents.length < minAreaSampleSize ||
    fee.basis !== 'percent'
  ) {
    return null;
  }

  const percentIncVat = fee.percentIncVat;

  const diff = compareFeeWithMedian({
    fee,
    medianPercentIncVat: median,
    monthlyRentPence,
  });

  if (
    diff == null ||
    diff.differencePence == null ||
    diff.differencePoints == null
  ) {
    return null;
  }

  const agenciesHigher = percents.filter(
    (value) => value > percentIncVat,
  ).length;
  const agenciesLower = percents.filter(
    (value) => value < percentIncVat,
  ).length;

  let dotPlotAriaLabel = `${agentName} ${formatFeePercent(percentIncVat)}, `;
  if (diff.differencePoints === 0) {
    dotPlotAriaLabel += `the same as the ${areaName} median`;
  } else if (diff.differencePoints > 0) {
    dotPlotAriaLabel += `higher than ${agenciesLower} of ${percents.length} ${areaName} agencies; median ${formatFeePercent(median)}`;
  } else {
    dotPlotAriaLabel += `lower than ${agenciesHigher} of ${percents.length} ${areaName} agencies; median ${formatFeePercent(median)}`;
  }

  const bandThresholds = getDirectoryFeeBandThresholds();

  return {
    differencePence: diff.differencePence,
    differencePoints: diff.differencePoints,
    band: getFeeBand(diff.differencePoints, bandThresholds),
    medianPercentIncVat: median,
    minimumPercentIncVat: minimum,
    maximumPercentIncVat: maximum,
    medianPosition: getPositionInRange({
      value: median,
      min: minimum,
      max: maximum,
    }),
    agentPosition: getPositionInRange({
      value: percentIncVat,
      min: minimum,
      max: maximum,
    }),
    percentsIncVat: percents,
    agenciesHigher,
    agenciesLower,
    dotPlotAriaLabel,
  };
}

export function getDirectoryAgentCost(options: {
  agentName: string;
  fees: BranchFees;
  service: DirectoryAgentCostService;
  monthlyRentPence: number;
  areaName: string | null;
  areaStats: AreaStatsForSummary | null | undefined;
}): DirectoryAgentCostView {
  const { agentName, fees, service, monthlyRentPence, areaName, areaStats } =
    options;

  if (!hasAnyPublishedFee(fees)) {
    const summary = summarizeAreaFees({
      stats: areaStats,
      service,
      monthlyRentPence,
    });
    const percentStats = getPercentStats(areaStats, service);
    const median = summary.medianPercentIncVat ?? 0;
    const minimum = percentStats?.minimumPercentIncVat ?? median;
    const maximum = percentStats?.maximumPercentIncVat ?? median;
    const percents = percentStats?.percentsIncVat ?? [];

    const medianCostPence =
      getPercentFeeAnnualCost({
        percentIncVat: median,
        monthlyRentPence,
      }) ?? 0;

    return {
      kind: 'noFees',
      areaName: areaName ?? 'this area',
      medianPercentIncVat: median,
      medianCostPence,
      minimumPercentIncVat: minimum,
      maximumPercentIncVat: maximum,
      medianPosition: getPositionInRange({
        value: median,
        min: minimum,
        max: maximum,
      }),
      percentsIncVat: percents,
    };
  }

  const classified = classifyAreaBranchFee({ fees, service });

  if (classified.status === 'serviceNotPublished') {
    return { kind: 'serviceNotPublished', areaName };
  }

  if (classified.status === 'notComparable') {
    const serviceFee = getServiceFee({ fees, service });
    const publishedPercent = parseFeePercent(serviceFee?.percent);
    const rawAmount = serviceFee?.amountPence;
    const publishedAmountPence =
      rawAmount == null || !Number.isFinite(rawAmount) ? null : rawAmount;

    return {
      kind: 'notComparable',
      reason: classified.reason,
      areaName,
      publishedPercent,
      publishedAmountPence,
    };
  }

  if (classified.status !== 'comparable') {
    return { kind: 'serviceNotPublished', areaName };
  }

  const { fee } = classified;
  const cost = getFeeCost({ fee, monthlyRentPence });
  const costPence = cost?.amountPence ?? 0;

  const percentIncVat = fee.basis === 'percent' ? fee.percentIncVat : 0;

  const publishedExVat = getFeeAtVatBasis({
    fee: getServiceFee({ fees, service }),
    feesIncludeVat: fees?.feesIncludeVat,
    vatBasis: 'exVat',
  });

  const publishedPercentExVat =
    publishedExVat.status === 'published' ? publishedExVat.percent : null;

  const percentStats = getPercentStats(areaStats, service);
  const comparison =
    areaName != null && percentStats != null && fee.basis === 'percent'
      ? buildComparison({
          agentName,
          areaName,
          fee,
          monthlyRentPence,
          stats: percentStats,
        })
      : null;

  return {
    kind: 'comparable',
    areaName,
    percentIncVat,
    publishedPercentExVat,
    isVatAdded: fee.isVatAdded,
    costPence,
    comparison,
    band: comparison?.band ?? null,
  };
}

export function formatDirectoryAgentCostHeadline(options: {
  agentName: string;
  service: DirectoryAgentCostService;
  view: DirectoryAgentCostView;
}): string {
  const { agentName, service, view } = options;
  const serviceLabel =
    service === 'management' ? 'full management' : 'rent collection';

  if (view.kind === 'noFees') {
    return `${agentName} doesn't publish its fees`;
  }

  if (view.kind === 'serviceNotPublished') {
    return `We can't compare ${agentName}'s ${serviceLabel} fee`;
  }

  if (view.kind === 'notComparable') {
    return `We can't compare ${agentName}'s ${serviceLabel} fee`;
  }

  if (view.comparison != null && view.areaName != null) {
    return formatFeeBandHeadline({
      band: view.comparison.band,
      areaName: view.areaName,
    });
  }

  return `What ${agentName} charges`;
}

export function formatFeePercentNote(options: {
  percentIncVat: number;
  publishedPercentExVat: number | null;
  isVatAdded: boolean;
}): string | null {
  const { percentIncVat, publishedPercentExVat, isVatAdded } = options;

  if (isVatAdded) {
    if (publishedPercentExVat == null) {
      return null;
    }

    const formatted = formatFeePercent(publishedPercentExVat).replace('%', '');

    return `${formatted}% + VAT`;
  }

  return `${formatFeePercent(percentIncVat).replace('%', '')}% inc VAT`;
}

export function formatVatFootnote(
  feesIncludeVat: boolean | null | undefined,
): string {
  if (feesIncludeVat === false) {
    return "All figures include VAT: the agent quotes without it, so we've added 20%.";
  }

  return 'All figures include VAT.';
}
